import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ContextualNav } from "~components/ContextualNav/ContextualNav";
import { ContextualNavWeekend } from "~components/ContextualNavWeekend";
import {
    contextualNavViewTracked,
    selectCurrentStory,
} from "~redux/features/stories-slice";
import {
    getRecordCollection,
    getWeekendCollection,
    Story,
    StoryCollection,
} from "~types/types";
import isEnabled from "~utils/features";
import { Tracker } from "~utils/tracker";
import { truthyFilter } from "~utils/truthyFilter";

import styles from "./ContextualNavContainer.module.css";

const tracker = new Tracker({
    category: "recirc",
});

interface ContextualNavContainerProps {
    firstStory: Story;
}

const IS_THE_WEEKEND_ENABLED = isEnabled("enableTheWeekend");

const ContextualNavContainer = ({
    firstStory,
}: ContextualNavContainerProps) => {
    const story = useSelector(selectCurrentStory) || firstStory;
    const recordCollection = getRecordCollection(story);
    const weekendCollection = getWeekendCollection(story);
    const isStoryInLatestWeekendCollection =
        weekendCollection?.slug === story.collections?.weekendId;
    const shouldRenderCollection =
        recordCollection ||
        (weekendCollection && isStoryInLatestWeekendCollection);
    const isWeekend =
        IS_THE_WEEKEND_ENABLED &&
        weekendCollection &&
        isStoryInLatestWeekendCollection;
    const dispatch = useDispatch();

    useEffect(() => {
        if (!shouldRenderCollection) {
            return;
        }

        if (!story.isContextualNavViewTracked) {
            const eventLabel = isWeekend
                ? `weekend_${story.collections?.weekendId}`
                : `record_${story.collections?.recordId}`;

            tracker.trackEvent({
                event: "contextualNavImpression",
                action: "impression",
                label: eventLabel,
            });
            dispatch(contextualNavViewTracked(story));
        }
    }, [story]);

    const trackingFunction = useCallback(
        (eventAction: string, eventLabel: string) => {
            tracker.trackEvent({
                event: "contextualNavClick",
                action: eventAction,
                label: eventLabel,
            });
        },
        []
    );

    if (!shouldRenderCollection) {
        return null;
    }

    const contextualNav = isWeekend ? weekendCollection : recordCollection;
    const { title, titleLink, slug, navLinks } =
        contextualNav as StoryCollection;
    const navItems = navLinks
        ?.filter(truthyFilter)
        .filter((navLink) => navLink.story || navLink.customUrl);

    return (
        <div
            className={styles.contextualNav}
            data-component="contextual-nav-container"
        >
            {isWeekend ? (
                <ContextualNavWeekend
                    navItems={navItems}
                    currentStoryId={story.id}
                    trackingFunction={trackingFunction}
                    weekendId={story.collections?.weekendId}
                />
            ) : (
                <ContextualNav
                    title={title}
                    titleLink={titleLink}
                    slug={slug}
                    navItems={navItems}
                    currentStoryId={story.id}
                    trackingFunction={trackingFunction}
                />
            )}
        </div>
    );
};

export default ContextualNavContainer;
